/**
 * AuthContext.tsx — Global authentication state for Shield
 *
 * Stores the current user, JWT tokens, and exposes login/logout/refresh.
 * Automatically refreshes the access token before expiry.
 * Adds the Bearer token to every axios request via an interceptor.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import axios from 'axios';
import API_BASE from '../config/api';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface AuthUser {
  id:                    string;
  email:                 string;
  full_name:             string;
  role:                  'customer' | 'engineer' | 'super_admin';
  customer_id:           string | null;
  is_verified:           boolean;
  is_active:             boolean;
  last_login_at:         string | null;
  account_status:        string | null;
  paused_reason:         string | null;
  force_password_change: boolean;
  billing_enabled:       boolean;
  impersonated_by?:      string;
  impersonated_by_email?: string;
}

export interface LoginResult {
  success:               boolean;
  error?:                string;
  error_code?:           string;
  locked_until?:         string;
  account_status?:       string;
  paused_reason?:        string;
  force_password_change?: boolean;
  billing_enabled?:      boolean;
}

export interface ImpersonationState {
  token:       string;
  targetName:  string;
  targetEmail: string;
  adminToken:  string;
  adminUser:   AuthUser;
}

interface AuthContextType {
  user:                 AuthUser | null;
  token:                string | null;
  loading:              boolean;
  login:                (email: string, password: string) => Promise<LoginResult>;
  logout:               () => Promise<void>;
  refreshToken:         () => Promise<void>;
  isAdmin:              boolean;
  isEngineer:           boolean;
  logoutMsg:            string | null;
  clearLogoutMsg:       () => void;
  impersonation:        ImpersonationState | null;
  startImpersonation:   (token: string, targetUser: AuthUser, adminToken: string, adminUser: AuthUser) => void;
  exitImpersonation:    () => void;
}

// ── Storage keys ──────────────────────────────────────────────────────────────

const ACCESS_KEY  = 'shield_access_token';
const REFRESH_KEY = 'shield_refresh_token';

// ── Role helpers ──────────────────────────────────────────────────────────────

const ROLE_RANK: Record<string, number> = {
  customer:    1,
  engineer:    2,
  super_admin: 3,
};

export function hasMinRole(user: AuthUser | null, minRole: string): boolean {
  if (!user) return false;
  return (ROLE_RANK[user.role] ?? 0) >= (ROLE_RANK[minRole] ?? 0);
}

// ── JWT payload decode (no verify — server handles that) ─────────────────────

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const [, payload] = token.split('.');
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return null;
  }
}

function tokenExpiresAt(token: string): number | null {
  const payload = decodeJwtPayload(token);
  return typeof payload?.exp === 'number' ? payload.exp * 1000 : null;
}

function payloadToUser(payload: Record<string, unknown>): AuthUser {
  return {
    id:                     String(payload.sub ?? ''),
    email:                  String(payload.email ?? ''),
    full_name:              String(payload.full_name ?? ''),
    role:                   (payload.role as AuthUser['role']) ?? 'customer',
    customer_id:            payload.customer_id ? String(payload.customer_id) : null,
    is_verified:            true,
    is_active:              true,
    last_login_at:          null,
    account_status:         payload.account_status ? String(payload.account_status) : null,
    paused_reason:          payload.paused_reason  ? String(payload.paused_reason)  : null,
    force_password_change:  Boolean(payload.force_password_change),
    billing_enabled:        Boolean(payload.billing_enabled),
    impersonated_by:        payload.impersonated_by        ? String(payload.impersonated_by)        : undefined,
    impersonated_by_email:  payload.impersonated_by_email  ? String(payload.impersonated_by_email)  : undefined,
  };
}

// ── Context ───────────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextType>({
  user:               null,
  token:              null,
  loading:            true,
  login:              async () => ({ success: false }),
  logout:             async () => {},
  refreshToken:       async () => {},
  isAdmin:            false,
  isEngineer:         false,
  logoutMsg:          null,
  clearLogoutMsg:     () => {},
  impersonation:      null,
  startImpersonation: () => {},
  exitImpersonation:  () => {},
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user,         setUser]         = useState<AuthUser | null>(null);
  const [token,        setToken]        = useState<string | null>(null);
  const [loading,      setLoading]      = useState(true);
  const [logoutMsg,    setLogoutMsg]    = useState<string | null>(null);
  const [impersonation, setImpersonation] = useState<ImpersonationState | null>(null);
  const refreshTimerRef                 = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Schedule auto-refresh 5 min before token expiry ──────────────────────

  const scheduleRefresh = useCallback((accessToken: string) => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    const expiresAt = tokenExpiresAt(accessToken);
    if (!expiresAt) return;
    const msUntilRefresh = expiresAt - Date.now() - 5 * 60 * 1000; // 5 min early
    if (msUntilRefresh <= 0) return;
    refreshTimerRef.current = setTimeout(async () => {
      try { await doRefresh(); } catch { /* will be caught by 401 interceptor */ }
    }, msUntilRefresh);
  }, []); // eslint-disable-line

  // ── Core refresh logic ────────────────────────────────────────────────────

  const doRefresh = useCallback(async () => {
    const stored = localStorage.getItem(REFRESH_KEY);
    if (!stored) throw new Error('No refresh token');
    const res = await axios.post(`${API_BASE}/auth/refresh`, { refresh_token: stored });
    const { access_token, refresh_token } = res.data;
    localStorage.setItem(ACCESS_KEY,  access_token);
    localStorage.setItem(REFRESH_KEY, refresh_token);
    setToken(access_token);
    const payload = decodeJwtPayload(access_token);
    if (payload) setUser(payloadToUser(payload));
    scheduleRefresh(access_token);
  }, [scheduleRefresh]);

  // ── Axios: inject Bearer token on every request ───────────────────────────

  useEffect(() => {
    const reqId = axios.interceptors.request.use((config) => {
      const t = localStorage.getItem(ACCESS_KEY);
      if (t && config.headers) {
        config.headers['Authorization'] = `Bearer ${t}`;
      }
      return config;
    });

    const resId = axios.interceptors.response.use(
      (r) => r,
      async (err) => {
        const orig = err.config;
        if (err.response?.status === 401 && !orig._retry) {
          orig._retry = true;
          try {
            await doRefresh();
            return axios(orig);
          } catch {
            setUser(null);
            setToken(null);
            localStorage.removeItem(ACCESS_KEY);
            localStorage.removeItem(REFRESH_KEY);
          }
        }
        return Promise.reject(err);
      }
    );

    return () => {
      axios.interceptors.request.eject(reqId);
      axios.interceptors.response.eject(resId);
    };
  }, [doRefresh]);

  // ── Initial load: restore from localStorage ───────────────────────────────

  useEffect(() => {
    const stored = localStorage.getItem(ACCESS_KEY);
    if (stored) {
      const expiresAt = tokenExpiresAt(stored);
      if (expiresAt && Date.now() < expiresAt) {
        setToken(stored);
        scheduleRefresh(stored);
        // Always verify with server before setting user — prevents closed/suspended
        // accounts from bypassing the login screen via a stored JWT.
        axios.get(`${API_BASE}/auth/me`).then((r) => {
          setUser(r.data as AuthUser);
        }).catch(() => {
          setToken(null);
          localStorage.removeItem(ACCESS_KEY);
          localStorage.removeItem(REFRESH_KEY);
          doRefresh().catch(() => { /* already cleared */ });
        }).finally(() => setLoading(false));
        return;
      }
    }
    // No valid stored token — try refresh anyway
    const refresh = localStorage.getItem(REFRESH_KEY);
    if (refresh) {
      doRefresh()
        .catch(() => {
          localStorage.removeItem(ACCESS_KEY);
          localStorage.removeItem(REFRESH_KEY);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  // ── Login ─────────────────────────────────────────────────────────────────

  const login = useCallback(async (email: string, password: string): Promise<LoginResult> => {
    try {
      const res = await axios.post(`${API_BASE}/auth/login`, { email, password });
      const { access_token, refresh_token, user: userData } = res.data;
      localStorage.setItem(ACCESS_KEY,  access_token);
      localStorage.setItem(REFRESH_KEY, refresh_token);
      setToken(access_token);
      setUser(userData as AuthUser);
      scheduleRefresh(access_token);
      return {
        success:               true,
        account_status:        res.data.account_status        ?? undefined,
        paused_reason:         res.data.paused_reason         ?? undefined,
        force_password_change: res.data.force_password_change ?? false,
        billing_enabled:       res.data.billing_enabled       ?? false,
      };
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'object') {
        return {
          success:      false,
          error:        detail.detail ?? 'Login failed',
          error_code:   detail.error_code,
          locked_until: detail.locked_until,
        };
      }
      return { success: false, error: detail ?? err.message ?? 'Login failed' };
    }
  }, [scheduleRefresh]);

  // ── Logout ────────────────────────────────────────────────────────────────

  const logout = useCallback(async () => {
    const wasPaused = user?.account_status === 'paused';
    const refresh = localStorage.getItem(REFRESH_KEY);
    if (refresh) {
      axios.post(`${API_BASE}/auth/logout`, { refresh_token: refresh }).catch(() => {});
    }
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
    setUser(null);
    setToken(null);
    if (wasPaused) {
      setLogoutMsg("Your data is safe. We'll keep everything for 30 days — come back anytime.");
    }
    window.location.href = '/';
  }, [user]);

  // ── refreshToken public API ───────────────────────────────────────────────

  const refreshToken = useCallback(async () => {
    await doRefresh();
  }, [doRefresh]);

  const isAdmin      = user?.role === 'super_admin';
  const isEngineer   = hasMinRole(user, 'engineer');
  const clearLogoutMsg = useCallback(() => setLogoutMsg(null), []);

  const startImpersonation = useCallback((
    impToken: string, targetUser: AuthUser, adminToken: string, adminUser: AuthUser
  ) => {
    // Swap active token to impersonation token
    localStorage.setItem(ACCESS_KEY, impToken);
    setToken(impToken);
    setUser(targetUser);
    setImpersonation({
      token:       impToken,
      targetName:  targetUser.full_name,
      targetEmail: targetUser.email,
      adminToken,
      adminUser,
    });
  }, []);

  const exitImpersonation = useCallback(() => {
    if (!impersonation) return;
    localStorage.setItem(ACCESS_KEY, impersonation.adminToken);
    setToken(impersonation.adminToken);
    setUser(impersonation.adminUser);
    setImpersonation(null);
  }, [impersonation]);

  return (
    <AuthContext.Provider value={{
      user, token, loading, login, logout, refreshToken,
      isAdmin, isEngineer, logoutMsg, clearLogoutMsg,
      impersonation, startImpersonation, exitImpersonation,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  return useContext(AuthContext);
}
